// The one client-side copy of the effective-availability rule, matching healthcare: (override.schedule_data ?? weekly[dayOfWeek]) minus blocks minus closures.
import { ScheduleOverride, TeamMember, TimeBlock, UnavailableBlock } from "../../types/admin/TeamTypes";
import { dayjs } from "../time";
import { wallClockMinutes } from "../schedule/wallClock";
import type { UserUuid } from "../../types/identity";

export const MINUTES_PER_DAY = 24 * 60;

// The closed taxonomy healthcare stores; an unknown value renders as `other` rather than uncoloured.
export const BLOCK_TYPES = ["lunch", "admin", "meeting", "training", "leave", "other"] as const;
export type BlockType = (typeof BLOCK_TYPES)[number];

export type UnavailableSource = "block" | "closure";

export interface Interval {
	// Minutes from the rendered day's local midnight, half-open [start, end).
	start: number;
	end: number;
}

export interface UnavailableInterval extends Interval {
	id: number;
	source: UnavailableSource;
	blockType: BlockType;
	reason: string | null;
}

// How the UI addresses one stored block: its id, its route (a null userId is the clinic-closure route) and the instants it really holds.
export interface BlockRef {
	id: number;
	userId: UserUuid | null;
	source: UnavailableSource;
	blockType: BlockType;
	reason: string | null;
	start: Date;
	end: Date;
}

// A block id and a closure id can collide, so the source is part of the key.
export const blockRefKey = (ref: Pick<BlockRef, "id" | "source">): string => `${ref.source}-${ref.id}`;

export interface DayAvailability {
	dayOff: boolean;
	dayOffReason: string | null;
	scheduled: Interval[];
	working: Interval[];
	offDuty: Interval[];
	unavailable: UnavailableInterval[];
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const WHOLE_DAY: Interval[] = [{ start: 0, end: MINUTES_PER_DAY }];

export const normaliseBlockType = (value: unknown): BlockType => {
	const key = String(value ?? "").trim().toLowerCase() as BlockType;

	return BLOCK_TYPES.includes(key) ? key : "other";
};

export const toDateKey = (date: Date): string =>
	`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export const intervalsContain = (intervals: Interval[], minute: number): boolean => intervals.some((interval) => minute >= interval.start && minute < interval.end);

export const mergeIntervals = (intervals: Interval[]): Interval[] =>
	intervals
		.filter((interval) => interval.end > interval.start)
		.sort((a, b) => a.start - b.start)
		.reduce<Interval[]>((acc, interval) => {
			const last = acc[acc.length - 1];
			if (last && interval.start <= last.end) {
				last.end = Math.max(last.end, interval.end);
				return acc;
			}
			acc.push({ start: interval.start, end: interval.end });
			return acc;
		}, []);

export interface WallClockWindow {
	start: string;
	end: string;
}

const wallClockLabel = (minutes: number): string => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

// Union of an override's schedule_data windows. WALL CLOCK "HH:mm", never the block path's instants.
// An overnight window has no day-relative interval to merge into, so it passes through rather than being dropped.
export const mergeWallClockWindows = (windows: { start?: string | null; end?: string | null }[]): WallClockWindow[] => {
	const overnight: WallClockWindow[] = [];
	const sameDay: Interval[] = [];

	windows.forEach((window) => {
		const start = wallClockMinutes(window.start);
		const end = wallClockMinutes(window.end);
		if (start === null || end === null || start === end) return;
		if (end < start) overnight.push({ start: wallClockLabel(start), end: wallClockLabel(end) });
		else sameDay.push({ start, end });
	});

	return [...mergeIntervals(sameDay).map((interval) => ({ start: wallClockLabel(interval.start), end: wallClockLabel(interval.end) })), ...overnight].sort((a, b) =>
		a.start.localeCompare(b.start)
	);
};

export const subtractIntervals = (from: Interval[], cuts: Interval[]): Interval[] => {
	const merged = mergeIntervals(cuts);

	return from.flatMap((interval) =>
		merged.reduce<Interval[]>(
			(pieces, cut) =>
				pieces.flatMap((piece) => {
					if (cut.end <= piece.start || cut.start >= piece.end) return [piece];
					const remainder: Interval[] = [];
					if (cut.start > piece.start) remainder.push({ start: piece.start, end: cut.start });
					if (cut.end < piece.end) remainder.push({ start: cut.end, end: piece.end });
					return remainder;
				}),
			[interval]
		)
	);
};

const readSchedule = (member: TeamMember | null | undefined): Record<string, unknown> | null => {
	const raw = member?.team_member_info?.availability_schedule;
	if (!raw) return null;

	if (typeof raw === "string") {
		try {
			return JSON.parse(raw);
		} catch {
			return null;
		}
	}

	return raw as unknown as Record<string, unknown>;
};

const weeklyBlocksFor = (member: TeamMember | null | undefined, date: Date): TimeBlock[] => {
	const schedule = readSchedule(member);
	if (!schedule) return [];

	const day = schedule[DAY_NAMES[date.getDay()]];
	if (Array.isArray(day)) return day as TimeBlock[];

	// Pre-migration rows still carry one { start, end } object per day rather than an array.
	return day && typeof day === "object" && "start" in day ? [day as TimeBlock] : [];
};

// That weekday's rostered windows — the base an "add availability" override is built on.
export const weeklyWindowsFor = (member: TeamMember | null | undefined, date: Date): WallClockWindow[] => mergeWallClockWindows(weeklyBlocksFor(member, date));

// ⚠️ CONVERSION BOUNDARY 1 of 2: TimeBlock.start/end are clinic-local "HH:mm" WALL CLOCK, read verbatim and never parsed as an instant.
const scheduleIntervals = (blocks: TimeBlock[]): Interval[] =>
	blocks.flatMap((block) => {
		const start = wallClockMinutes(block.start);
		const end = wallClockMinutes(block.end);
		if (start === null || end === null || start === end) return [];

		// An end before the start is an overnight roster, split at midnight — the same reading isWithinWallClockBlock takes.
		return end > start ? [{ start, end }] : [{ start, end: MINUTES_PER_DAY }, { start: 0, end }];
	});

// ⚠️ CONVERSION BOUNDARY 2 of 2: UnavailableBlock.starts_at/ends_at are ISO-8601 UTC INSTANTS, converted here to minutes from the rendered day's local midnight and clipped to it.
// Known limitation, do not fix here: the grid renders in the BROWSER zone, so this is exact in the clinic's own timezone and skewed outside it — same as the calendar it replaces (86d42y068).
const instantInterval = (block: UnavailableBlock, dayStart: number): Interval | null => {
	const from = dayjs(block.starts_at).valueOf();
	const to = dayjs(block.ends_at).valueOf();
	if (!Number.isFinite(from) || !Number.isFinite(to)) return null;

	const start = Math.max(0, Math.round((from - dayStart) / 60000));
	const end = Math.min(MINUTES_PER_DAY, Math.round((to - dayStart) / 60000));

	return end > start ? { start, end } : null;
};

const toUnavailable = (block: UnavailableBlock, dayStart: number, source: UnavailableSource): UnavailableInterval | null => {
	const interval = instantInterval(block, dayStart);

	return interval ? { ...interval, id: block.id, source, blockType: normaliseBlockType(block.block_type), reason: block.reason ?? null } : null;
};

// start_date/end_date can arrive as a bare date or a full timestamp; only the calendar day is meaningful.
export const overrideFor = (overrides: ScheduleOverride[] | undefined, date: Date): ScheduleOverride | null => {
	if (!overrides || overrides.length === 0) return null;
	const key = toDateKey(date);

	return overrides.find((override) => String(override.start_date).slice(0, 10) <= key && key <= String(override.end_date).slice(0, 10)) ?? null;
};

export function resolveDayAvailability(
	member: TeamMember | null | undefined,
	date: Date,
	overrides?: ScheduleOverride[],
	blocks?: UnavailableBlock[],
	closures?: UnavailableBlock[]
): DayAvailability {
	const override = overrideFor(overrides, date);
	const dayOff = Boolean(override && (override.override_type === "block" || override.schedule_data === null));

	const source = dayOff ? [] : override?.schedule_data ?? weeklyBlocksFor(member, date);
	const scheduled = mergeIntervals(scheduleIntervals(source));

	const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
	const unavailable = [...(blocks ?? []).map((block) => toUnavailable(block, dayStart, "block")), ...(closures ?? []).map((block) => toUnavailable(block, dayStart, "closure"))]
		.filter((interval): interval is UnavailableInterval => interval !== null)
		.sort((a, b) => a.start - b.start || a.end - b.end);

	return {
		dayOff,
		dayOffReason: dayOff ? override?.reason ?? null : null,
		scheduled,
		working: subtractIntervals(scheduled, unavailable),
		offDuty: subtractIntervals(WHOLE_DAY, scheduled),
		unavailable,
	};
}
